import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { compareAsc } from 'date-fns';
import isEqual from 'lodash.isequal';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import type { Sport } from '@sorare/core/src/__generated__/globalTypes';
import {
  Rarity,
  SeasonEligibility,
  So5LeaderboardSeasonality,
} from '@sorare/core/src/__generated__/globalTypes';
import { ChevronRightBold } from '@sorare/core/src/atoms/icons/ChevronRightBold';
import { SBHorizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelM } from '@sorare/core/src/atoms/typography';
import { ANY_SPORT_PLAYERS_SLUG_CARDS_SALES_HISTORY } from '@sorare/core/src/constants/__generated__/routes';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import {
  SaleType,
  useCardsOnSale,
} from '@sorare/core/src/hooks/search/useCardsOnSale';
import { useCurrentUserCurrencies } from '@sorare/core/src/hooks/useCurrentUserCurrencies';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import { joinFiltersWithAnd } from '@sorare/core/src/lib/algolia';
import { navLabels } from '@sorare/core/src/lib/glossary';
import type { StatsRarity } from '@sorare/core/src/lib/playerSearchView';
import { PlayerSearchViewAttributes } from '@sorare/core/src/lib/playerSearchView';
import { generateSportPath } from '@sorare/core/src/lib/routing/generateSportPath';
import type {
  ClickAddToCart_Origin,
  ClickInstantBuy_Flow,
} from '@sorare/core/src/protos/events/so5/shared/events';

import { Auctions } from '#src/components/player/PlayerMarketDrawer/Auctions/index.tsx';
import { CollectionHeader } from '#src/components/player/PlayerMarketDrawer/CollectionHeader/index.tsx';
import { Empty } from '#src/components/player/PlayerMarketDrawer/Empty/index.tsx';
import { PlayerMarketDrawerFooter } from '#src/components/player/PlayerMarketDrawer/Footer/index.tsx';
import { ManagerSales } from '#src/components/player/PlayerMarketDrawer/ManagerSales/index.tsx';
import { PlayerPageHeader } from '#src/components/player/PlayerMarketDrawer/PlayerPageHeader/index.tsx';
import { ScoutingCenterHeader } from '#src/components/player/PlayerMarketDrawer/ScoutingCenterHeader/index.tsx';
import {
  Block,
  PlayerDrawerTokenSkeleton,
  Root,
  StyledLink,
  Title,
} from '#src/components/player/PlayerMarketDrawer/ui.tsx';
import { LazyPriceGraph } from '#src/components/price/LazyPriceGraph/index.tsx';
import { PlayerDrawerToken } from '#src/components/token/PlayerDrawerToken/index.tsx';
import { isPrimaryBuyLive } from '#src/hooks/primaryBuy/useIsPrimaryBuyLive.ts';
import { usePlayerPageFilters } from '#src/hooks/usePlayerPageFilters.ts';
import type { SeasonEligibilityOptions } from '#src/lib/seasonalities.ts';
import {
  getTimeframeFromRarity,
  timeframeMessages,
} from '#src/lib/timeframe.ts';
import { useManagePlayerSearchView } from '#src/searchCommonPlayer/StatsManager/useManagePlayerSearchView.ts';

import type {
  PlayerMarketDrawerContentQuery,
  PlayerMarketDrawerContentQueryVariables,
} from './__generated__/index.graphql.ts';
import { useSortSecondaryOfferSlugs } from './useSortSecondaryOfferSlugs.tsx';

const optionalFilters = joinFiltersWithAnd([
  `sale.type:${SaleType.PrimaryOffer}`,
  `sale.type:${SaleType.EnglishAuction}`,
]);

type Card = PlayerMarketDrawerContentQuery['anyCards'][number];

const PLAYER_MARKET_DRAWER_QUERY = gql`
  query PlayerMarketDrawerContentQuery(
    $slugs: [String!]!
    $googlePlayStoreCountryCode: String
    $isAndroidApp: Boolean = false
    $isIosApp: Boolean = false
    $onlyPrimary: Boolean = false
    $withCollectionPredictions: Boolean = true
  ) {
    anyCards(slugs: $slugs) {
      slug
      ...PlayerMarketDrawerAuctions_anyCard
      ...PlayerDrawerToken_anyCard
      ...PlayerMarketDrawerManagerSales_anyCard
      ...useSortSecondaryOfferSlugs_anyCard
    }
  }
  ${Auctions.fragments.anyCard}
  ${PlayerDrawerToken.fragments.anyCard}
  ${ManagerSales.fragments.anyCard}
  ${useSortSecondaryOfferSlugs.fragments.anyCard}
` as TypedDocumentNode<
  PlayerMarketDrawerContentQuery,
  PlayerMarketDrawerContentQueryVariables
>;

export enum Variant {
  SCOUTING_CENTER = 'SCOUTING_CENTER',
  COLLECTION = 'COLLECTION',
  PLAYER_PAGE = 'PLAYER_PAGE',
  MARKET_HOME = 'MARKET_HOME',
}

const inSeasonEligible: SeasonEligibilityOptions[] = [
  SeasonEligibility.IN_SEASON,
  So5LeaderboardSeasonality.IN_SEASON,
];

type Props = {
  variant: Variant;
  playerSlug: string | null;
  sport: Sport;
  filters?: {
    seasonality: SeasonEligibility;
    rarity: StatsRarity;
  };
  collection?: {
    rarity?: Rarity | null;
    playerName?: string | null;
    teamSlug?: string | null;
    teamName?: string | null;
    seasonStartYear?: number;
    seasonName?: string;
    link?: string;
  };
  skip?: boolean;
  canUseConversionCredit?: boolean;
  onClose?: () => void;
  origin?: {
    instantBuy: ClickInstantBuy_Flow;
    addToCart: ClickAddToCart_Origin;
  };
  withFooter?: ReactNode;
  featurePrimaryOffer?: boolean;
};

export const Content = ({
  playerSlug,
  filters: initialFilters,
  variant,
  sport,
  collection,
  skip,
  canUseConversionCredit,
  onClose,
  origin,
  withFooter,
  featurePrimaryOffer,
}: Props) => {
  const { statsManager } = useManagePlayerSearchView();
  const { mainCurrencySymbol, currency } = useCurrentUserCurrencies();
  const { filters: playerPageFilters } = usePlayerPageFilters(sport);
  const filters: {
    seasonality: SeasonEligibilityOptions;
    rarity: StatsRarity;
  } = (() => {
    switch (variant) {
      case Variant.PLAYER_PAGE:
        return playerPageFilters;
      case Variant.MARKET_HOME:
        return (
          initialFilters ?? {
            seasonality: SeasonEligibility.IN_SEASON,
            rarity: Rarity.limited,
          }
        );
      default:
        return (
          initialFilters ?? {
            seasonality: statsManager[PlayerSearchViewAttributes.SEASONALITY],
            rarity: statsManager[PlayerSearchViewAttributes.RARITY],
          }
        );
    }
  })();

  const { googlePlayStoreCountryCode, isAndroidApp, isIosApp } =
    useIsMobileApp();

  const [{ seasonality, rarity }, setFilters] = useState(filters);

  const cardIndex = statsManager[PlayerSearchViewAttributes.CARDS_SORT];

  const [{ auctions, primaryOfferLive }, setCards] = useState<{
    auctions: Card[];
    primaryOfferLive: Card | undefined;
  }>({
    auctions: [],
    primaryOfferLive: undefined,
  });

  const {
    loading: algoliaLoading,
    slugs,
    auctionSlugs,
    primaryOfferSlugs,
    secondaryOfferSlugs: secondaryOfferSlugsFromAlgolia,
    nbHits,
  } = useCardsOnSale({
    ...(playerSlug && { playerSlugs: [playerSlug] }),
    ...(collection
      ? {
          rarities: [collection.rarity || Rarity.limited],
          season: collection.seasonStartYear,
          teamSlug: collection.teamSlug,
        }
      : {
          rarities: [rarity],
          inSeasonEligible: inSeasonEligible.includes(seasonality),
        }),
    limit: 40,
    index: cardIndex,
    optionalFilters,
    skip,
  });

  const managerSalesCount =
    nbHits - auctionSlugs.length - primaryOfferSlugs.length;

  const skipGql = algoliaLoading || skip;
  const { data, loading: gqlLoading } = useQuery(PLAYER_MARKET_DRAWER_QUERY, {
    variables: {
      slugs,
      googlePlayStoreCountryCode,
      isAndroidApp,
      isIosApp,
      onlyPrimary: false,
    },
    skip: skipGql || !slugs.length,
  });

  const secondaryOfferSlugs = useSortSecondaryOfferSlugs({
    slugs: secondaryOfferSlugsFromAlgolia,
    anyCards: data?.anyCards || [],
    cardIndex,
  });

  const primaryOfferLoading =
    !!primaryOfferSlugs.length && (algoliaLoading || gqlLoading);

  const managerSales = secondaryOfferSlugs
    .map(s => data?.anyCards.find(c => c.slug === s))
    .filter(Boolean);

  const noCardsOnSale =
    !algoliaLoading &&
    !auctionSlugs.length &&
    !primaryOfferSlugs.length &&
    !secondaryOfferSlugs.length;

  if (data?.anyCards && ((!algoliaLoading && !gqlLoading) || skipGql)) {
    const auctionCards = data.anyCards.filter(
      ({ slug, latestEnglishAuction }) =>
        auctionSlugs.includes(slug) && latestEnglishAuction
    );
    const auctionCardsByAscendingEndDate = auctionCards.sort((a, b) =>
      compareAsc(
        a.latestEnglishAuction!.endDate,
        b.latestEnglishAuction!.endDate
      )
    );
    const livePrimaryOfferCard = data.anyCards.find(
      card => primaryOfferSlugs.includes(card.slug) && isPrimaryBuyLive(card)
    );

    if (
      !isEqual(auctions, auctionCardsByAscendingEndDate) ||
      !isEqual(primaryOfferLive, livePrimaryOfferCard)
    ) {
      setCards({
        auctions: auctionCardsByAscendingEndDate,
        primaryOfferLive: livePrimaryOfferCard,
      });
    }
  }

  return (
    <Root className={variant} gap={variant === Variant.PLAYER_PAGE ? 1.5 : 0}>
      {!!collection && variant === Variant.COLLECTION && (
        <CollectionHeader
          seasonName={collection.seasonName}
          playerName={collection.playerName}
          teamName={collection.teamName}
          rarity={collection.rarity}
          link={collection.link}
          onClose={onClose}
        />
      )}
      {[Variant.SCOUTING_CENTER, Variant.MARKET_HOME].includes(variant) && (
        <ScoutingCenterHeader
          filters={{ seasonality, rarity }}
          setRarity={(r: StatsRarity) => {
            setFilters(prev => ({ ...prev, rarity: r }));
          }}
          setSeasonality={s => {
            setFilters(prev => ({ ...prev, seasonality: s }));
          }}
          onClose={onClose}
        />
      )}
      {variant === Variant.PLAYER_PAGE && (
        <PlayerPageHeader
          filters={{ seasonality, rarity }}
          setFilters={setFilters}
          sport={sport}
        />
      )}
      <Block>
        <StyledLink
          to={generateSportPath(ANY_SPORT_PLAYERS_SLUG_CARDS_SALES_HISTORY, {
            params: { slug: playerSlug || '' },
            sport,
          })}
          className={Variant.PLAYER_PAGE === variant ? 'withBorder' : ''}
        >
          <Vertical>
            <SBHorizontal>
              <Title>
                <FormattedMessage {...navLabels.salesHistory} />
                <LabelM as="span" color="var(--c-nd-600)">
                  <FormattedMessage
                    {...timeframeMessages[getTimeframeFromRarity(rarity)]}
                  />
                </LabelM>
                <LabelM as="span" color="var(--c-nd-600)">
                  ({mainCurrencySymbol})
                </LabelM>
              </Title>
              <ChevronRightBold />
            </SBHorizontal>
            <LazyPriceGraph
              small
              playerSlug={playerSlug || ''}
              filters={{
                rarity,
                seasonEligibility:
                  seasonality === So5LeaderboardSeasonality.IN_SEASON
                    ? SeasonEligibility.IN_SEASON
                    : SeasonEligibility.CLASSIC,
                withSeasonEligibility: true,
                timeframe: getTimeframeFromRarity(rarity),
              }}
              currency={currency}
            />
          </Vertical>
        </StyledLink>
      </Block>
      {!!noCardsOnSale && <Empty />}
      <Auctions
        slugs={auctionSlugs}
        anyCards={auctions}
        canUseConversionCredit={canUseConversionCredit}
      />
      {!!(primaryOfferLive || primaryOfferLoading) && (
        <Block>
          <LabelM color="var(--c-nd-600)">
            <FormattedMessage defaultMessage="New Card" />
          </LabelM>
          {primaryOfferLive ? (
            <PlayerDrawerToken
              anyCard={primaryOfferLive}
              bestValue
              canUseConversionCredit={canUseConversionCredit}
              origin={origin}
              featured={!!featurePrimaryOffer}
            />
          ) : (
            <PlayerDrawerTokenSkeleton />
          )}
        </Block>
      )}
      <ManagerSales
        anyCards={managerSales}
        slugs={secondaryOfferSlugs}
        count={managerSalesCount}
        primaryOffer={primaryOfferLive}
      />
      {!!withFooter && !!playerSlug && (
        <PlayerMarketDrawerFooter playerSlug={playerSlug} sport={sport} />
      )}
    </Root>
  );
};
